import {
  ApplicationRef,
  computed,
  createComponent,
  DestroyRef,
  ElementRef,
  EnvironmentInjector,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TourPopupComponent } from './tour-popup/tour-popup.component';
import {PostHogService} from '../services/posthog.service';

export type TourPosition =
  | 'top' | 'top-start' | 'top-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'left' | 'left-start' | 'left-end'
  | 'right' | 'right-start' | 'right-end';

export interface TourButton {
  text?: string;
  icon?: string;
  /** Called when the button is clicked. Use a closure to reference your TourService. */
  action: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface TourStep {
  id?: string;
  title?: string;
  text: string;
  /**
   * Element to anchor the popup to.
   * `element` accepts any CSS selector ('#id', '.class', 'tag') or a '@directiveId'
   * to reference an element registered via the [tourStep] directive.
   */
  attachTo?: { element: string; on?: TourPosition };
  /** Custom button row. When omitted, default Skip / Prev / Next buttons are shown. */
  buttons?: TourButton[]|null;
  /** Called instead of tourService.next() when the default Next button is clicked. */
  onNext?: () => void;
  /** Called instead of tourService.prev() when the default Prev button is clicked. */
  onPrev?: () => void;
  /** Show the directional arrow. Default: true. */
  arrow?: boolean;
  /** Scroll the target into view. Default: true. */
  scrollTo?: boolean;
  /** Extra CSS classes added to the popup panel. */
  classes?: string;
  /** Skip this step when it returns false. Evaluated at navigation time. */
  canShow?: () => boolean;
  /** Resolved before the step is shown. */
  beforeShowPromise?: () => Promise<void>;
}

export interface TourOptions {
  /** Whether to show the click-blocking backdrop. Default: true. */
  hasBackdrop?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private posthogService = inject(PostHogService);

  private registry = new Map<string, TourStep[]>();
  private optionsRegistry = new Map<string, TourOptions>();
  private elementMap = new Map<string, ElementRef>();
  private popup: TourPopupComponent | undefined;

  private _activeTour = signal<string | null>(null);
  private _currentStepIndex = signal<number>(-1);

  readonly activeTour = this._activeTour.asReadonly();
  readonly currentStepIndex = this._currentStepIndex.asReadonly();

  readonly isActive = computed(() => this._activeTour() !== null && this._currentStepIndex() >= 0);

  readonly currentStep = computed((): TourStep | null => {
    const tour = this._activeTour();
    const idx = this._currentStepIndex();
    if (!tour || idx < 0) return null;
    return this.registry.get(tour)?.[idx] ?? null;
  });

  readonly isFirstVisibleStep = computed((): boolean => {
    const tour = this._activeTour();
    const idx = this._currentStepIndex();
    if (!tour || idx < 0) return true;
    return !(this.registry.get(tour) ?? []).slice(0, idx).some(s => (s.canShow?.() ?? true));
  });

  readonly nextStep = computed((): TourStep | null => {
    const tour = this._activeTour();
    const idx = this._currentStepIndex();
    if (!tour || idx < 0) return null;
    const steps = this.registry.get(tour) ?? [];
    let nextIdx = idx + 1;
    while (nextIdx < steps.length && !(steps[nextIdx].canShow?.() ?? true)) nextIdx++;
    return nextIdx < steps.length ? steps[nextIdx] : null;
  });

  readonly isLastVisibleStep = computed((): boolean => {
    const tour = this._activeTour();
    const idx = this._currentStepIndex();
    if (!tour || idx < 0) return true;
    return !(this.registry.get(tour) ?? []).slice(idx + 1).some(s => (s.canShow?.() ?? true));
  });

  readonly currentVisibleStepNumber = computed((): number => {
    const tour = this._activeTour();
    const idx = this._currentStepIndex();
    if (!tour || idx < 0) return 0;
    return (this.registry.get(tour) ?? []).slice(0, idx + 1).filter(s => (s.canShow?.() ?? true)).length;
  });

  readonly visibleStepCount = computed((): number => {
    const tour = this._activeTour();
    if (!tour) return 0;
    return (this.registry.get(tour) ?? []).filter(s => (s.canShow?.() ?? true)).length;
  });

  readonly activeOptions = computed((): TourOptions => {
    const tour = this._activeTour();
    if (!tour) return {};
    return this.optionsRegistry.get(tour) ?? {};
  });

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {
    inject(Router).events
      .pipe(
        filter(e => e instanceof NavigationStart),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => {
        if (this.isActive()) this.stop();
      });
  }

  register(name: string, steps: TourStep[], options?: TourOptions): void {
    this.registry.set(name, steps);
    if (options) this.optionsRegistry.set(name, options);
  }

  start(name: string): void {
    this.posthogService.posthog.capture('tour_started', {tour: name});

    const steps = this.registry.get(name);
    if (!steps?.length) return;

    this._activeTour.set(name);
    const firstIdx = steps.findIndex(s => (s.canShow?.() ?? true));
    if (firstIdx === -1) {
      this._activeTour.set(null);
      return;
    }

    this._currentStepIndex.set(firstIdx);
    this.showCurrentStep();
  }

  next(): void {
    if (!this.isActive()) return;
    const tour = this._activeTour()!;
    const steps = this.registry.get(tour)!;
    const current = this._currentStepIndex();

    let nextIdx = current + 1;
    while (nextIdx < steps.length && !(steps[nextIdx].canShow?.() ?? true)) nextIdx++;

    if (nextIdx >= steps.length) {
      this.stop();
      return;
    }

    this._currentStepIndex.set(nextIdx);
    this.showCurrentStep();
  }

  prev(): void {
    if (!this.isActive()) return;
    const tour = this._activeTour()!;
    const steps = this.registry.get(tour)!;
    const current = this._currentStepIndex();

    let prevIdx = current - 1;
    while (prevIdx >= 0 && !(steps[prevIdx].canShow?.() ?? true)) prevIdx--;

    if (prevIdx < 0) return;

    this._currentStepIndex.set(prevIdx);
    this.showCurrentStep();
  }

  stop(): void {
    setTimeout(() => {
      this._activeTour.set(null);
      this._currentStepIndex.set(-1);
    }, 200)
    this.popup?.close();
  }

  registerElement(id: string, el: ElementRef): void {
    this.elementMap.set(id, el);
  }

  unregisterElement(id: string): void {
    this.elementMap.delete(id);
  }

  getElement(id: string): ElementRef | undefined {
    return this.elementMap.get(id);
  }

  resolveElement(step: TourStep): ElementRef | undefined {
    const sel = step.attachTo?.element;
    if (!sel) return undefined;
    const node = this.queryElement(sel);
    return node ? new ElementRef(node) : undefined;
  }

  hasRegisteredTours() {
    return this.registry.size > 0;
  }

  /** Resolve a selector: '@id' → directive map, otherwise → document.querySelector */
  private queryElement(selector: string): Element | null {
    if (selector.startsWith('@')) return this.elementMap.get(selector.slice(1))?.nativeElement ?? null;
    return document.querySelector(selector);
  }

  private async showCurrentStep(): Promise<void> {
    const step = this.currentStep();
    if (!step) return;

    if (step.beforeShowPromise) {
      await step.beforeShowPromise();
      // Bail if the tour was stopped or advanced while waiting
      if (!this.isActive() || this.currentStep() !== step) return;
    }

    const el = this.resolveElement(step);
    if ((step.scrollTo ?? true) && el?.nativeElement) {
      el.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }

    if (!this.popup) {
      this.createPopup();
      return;
    }

    this.popup.open(el, step.attachTo?.on);
  }

  private createPopup(): void {
    const ref = createComponent(TourPopupComponent, { environmentInjector: this.injector });
    this.appRef.attachView(ref.hostView);
    document.body.appendChild(ref.location.nativeElement);
    this.popup = ref.instance;

    // Delay one tick so ngAfterViewInit completes before open() is called
    setTimeout(() => {
      const step = this.currentStep();
      if (step && this.isActive()) {
        this.popup!.open(this.resolveElement(step), step.attachTo?.on);
      }
    });
  }
}
